import { FC, MouseEvent, useEffect, useRef } from 'react';
import { Instance } from 'tippy.js';
import VerticalNavigationMenuOption from 'components/navigation/verticalNavigation/VerticalNavigationMenuOption';
import InAppNotificationsIcon from 'components/in-app-notifications/Icon';
import useInAppNotificationsCountQuery from 'app/modules/inAppNotifications/hooks/useInAppNotificationsCountQuery';
import { VerticalMenuItemWrapper } from '../VerticalMenuItemWrapper';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@kontentino/ui';
import NotificationsTooltipContent from 'components/navigation/verticalNavigation/userActionsMenu/notifications/NotificationsTooltipContent';
import Link from 'components/shared/Link';
import routes from 'constants/routes';
import useBoolean from 'utils/hooks/useBoolean';
import { useMarkAllNotificationsAsSeen } from 'app/modules/inAppNotifications/hooks/useInAppNotificationActions';

type Props = {};

const MARK_AS_SEEN_DELAY = 1000;

const Notifications: FC<Props> = () => {
  const { data } = useInAppNotificationsCountQuery();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useBoolean(false);
  const { mutate: markAllAsSeen } = useMarkAllNotificationsAsSeen();
  const hasMarkedAsSeen = useRef(false);
  const tippyRef = useRef<Instance | null>(null);

  const unseenCount = data?.count ?? 0;

  useEffect(() => {
    if (!isOpen) {
      hasMarkedAsSeen.current = false;
      return;
    }

    if (unseenCount === 0 || hasMarkedAsSeen.current) {
      return;
    }

    const timeout = setTimeout(() => {
      hasMarkedAsSeen.current = true;
      markAllAsSeen();
    }, MARK_AS_SEEN_DELAY);

    return () => clearTimeout(timeout);
  }, [isOpen, unseenCount, markAllAsSeen]);

  const closePanel = () => tippyRef.current?.hide();

  const closePanelOnLinkClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target instanceof Element && event.target.closest('a')) {
      closePanel();
    }
  };

  return (
    <Tooltip
      content={
        <div className="tw-h-[min(560px,80vh)]" onClick={closePanelOnLinkClick}>
          {isOpen && (
            <NotificationsTooltipContent onRequestClose={closePanel} />
          )}
        </div>
      }
      placement="right-start"
      interactive
      interactiveBorder={10}
      interactiveDebounce={100}
      className="tw-w-[400px] !tw-max-w-none tw-border !tw-border-grayscale-10 !tw-bg-transparent !tw-p-0"
      onCreate={(instance) => {
        tippyRef.current = instance;
      }}
      onShow={() => setIsOpen.on()}
      onHidden={() => setIsOpen.off()}
    >
      <Link
        to={routes.IN_APP_NOTIFICATIONS}
        data-name="navbar_notifications"
        isRouterLink
      >
        <VerticalMenuItemWrapper isActiveMenu={isOpen}>
          <VerticalNavigationMenuOption
            disableTooltip
            label={t('navigation.userSubActions.notifications')}
            icon={<InAppNotificationsIcon notificationsCount={unseenCount} />}
          />
        </VerticalMenuItemWrapper>
      </Link>
    </Tooltip>
  );
};

export default Notifications;
